/**
 * Permission checking utilities for role-based access control
 */
import { EXPLICIT_ONLY_PERMISSIONS } from '@efacture/core';

/**
 * Check if the user has a specific permission
 * @param permissions - Array of user's permissions
 * @param required - The required permission to check
 * @returns true if the user has the permission
 */
export function hasPermission(
  permissions: readonly string[],
  required: string
): boolean {
  // An explicit-only permission is held by its own code alone: neither `*`,
  // a group wildcard nor a scoped variant grants it (same rule as the API's
  // matcher, @efacture/core permission-matcher.ts).
  if (EXPLICIT_ONLY_PERMISSIONS.has(required)) {
    return permissions.includes(required);
  }

  // Check for wildcard permission (admin)
  if (permissions.includes('*')) {
    return true;
  }

  // Direct match
  if (permissions.includes(required)) {
    return true;
  }

  // Check for wildcard patterns (e.g., "invoices:*" matches "invoices:create")
  const [resource, action] = required.split(':');
  if (permissions.includes(`${resource}:*`)) {
    return true;
  }

  // Check for scoped permissions (e.g., "invoices:read:own" grants "invoices:read")
  // A user with "invoices:read:own" can see the invoices menu
  if (action && permissions.some((p) => p.startsWith(`${resource}:${action}:`))) {
    return true;
  }

  return false;
}

/**
 * Check if the user has any of the specified permissions
 * @param permissions - Array of user's permissions
 * @param required - Array of permissions where at least one is required
 * @returns true if the user has at least one of the permissions
 */
export function hasAnyPermission(
  permissions: readonly string[],
  required: readonly string[]
): boolean {
  return required.some((perm) => hasPermission(permissions, perm));
}

/**
 * Check if the user has all of the specified permissions
 * @param permissions - Array of user's permissions
 * @param required - Array of permissions that are all required
 * @returns true if the user has all of the permissions
 */
export function hasAllPermissions(
  permissions: readonly string[],
  required: readonly string[]
): boolean {
  return required.every((perm) => hasPermission(permissions, perm));
}

/**
 * Common permission constants
 */
export const Permissions = {
  // Hospitality: folios of stays opened by another user
  HOSPITALITE_SEJOURS: 'hospitalite:sejours',
  // Invoice permissions
  INVOICE_CREATE: 'invoices:create',
  INVOICE_READ: 'invoices:read',
  INVOICE_UPDATE: 'invoices:update',
  INVOICE_DELETE: 'invoices:delete',
  INVOICE_VOID: 'invoices:void',
  INVOICE_EXPORT: 'invoices:export',
  // Added for the invoice screens' action gates. Each one is the string the
  // API route actually enforces, read from the route rather than guessed:
  //   invoices:sign        → POST /api/invoices/:id/sign
  //   invoices:credit-note → POST /api/invoices/:id/credit-note
  // (packages/api/src/routes/invoices/index.ts, authorize* middlewares).
  INVOICE_SIGN: 'invoices:sign',
  INVOICE_CREDIT_NOTE: 'invoices:credit-note',
  //   invoices:payment-cancel → POST /api/invoices/:id/payments/:paymentId/cancel
  INVOICE_PAYMENT_CANCEL: 'invoices:payment-cancel',
  //   invoices:original-release (or invoices:create) → POST /api/invoices/:id/original-release
  INVOICE_ORIGINAL_RELEASE: 'invoices:original-release',

  // Client permissions
  CLIENT_CREATE: 'clients:create',
  CLIENT_READ: 'clients:read',
  CLIENT_UPDATE: 'clients:update',
  CLIENT_DELETE: 'clients:delete',

  // Product permissions
  PRODUCT_CREATE: 'products:create',
  PRODUCT_READ: 'products:read',
  PRODUCT_UPDATE: 'products:update',
  PRODUCT_DELETE: 'products:delete',

  // Report permissions
  REPORT_VIEW: 'reports:view',
  REPORT_EXPORT: 'reports:export',
  REPORT_FEC: 'reports:fec',

  // Settings permissions
  SETTINGS_VIEW: 'settings:view',
  SETTINGS_UPDATE: 'settings:update',

  // User management permissions
  USER_CREATE: 'users:create',
  USER_READ: 'users:read',
  USER_UPDATE: 'users:update',
  USER_DELETE: 'users:delete',

  // MCF permissions
  MCF_VIEW: 'mcf:view',
  MCF_CONFIGURE: 'mcf:configure',
  // Outage declaration and its notification (arrêté n°2025-0049, art. 7 c)).
  MCF_DECLARER_PANNE: 'mcf:declarer-panne',

  // PI-SPI (BCEAO) permissions — `POST /api/pispi/rtp` and
  // `GET /api/pispi/alias-lookup` (packages/api/src/routes/pispi/index.ts:174).
  // Named here because the button that needs it lives on an INVOICE screen.
  PISPI_REQUEST_PAYMENT: 'pispi:request-payment',
  // `GET /api/pispi/events` and `POST /api/pispi/events/:id/resolve`
  // (packages/api/src/routes/pispi/index.ts). Gates the admin screen that
  // reconciles PAIEMENT_RECU events the automatic retry gave up on.
  PISPI_CONFIGURE: 'pispi:configure',

  // Admin wildcard
  ADMIN: '*',
} as const;

export type Permission = (typeof Permissions)[keyof typeof Permissions];
